import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { API } from "@/App";
import { toast } from "sonner";
import {
  Mountain, ArrowRight, ArrowLeft, CheckCircle2,
  Target, Users, Brain, Zap, RefreshCw, Mail, ChevronRight, ClipboardList
} from "lucide-react";

const categoryIcons = {
  "Communication & Influence": Zap,
  "Strategic Thinking": Target,
  "Team Building & Empowerment": Users,
  "Emotional Intelligence": Brain,
  "Change Management": RefreshCw,
  "Self-Assessment": ClipboardList,
};

const categoryColors = {
  "Communication & Influence": "bg-orange-500",
  "Strategic Thinking": "bg-emerald-500",
  "Team Building & Empowerment": "bg-blue-500",
  "Emotional Intelligence": "bg-purple-500",
  "Change Management": "bg-teal-500",
  "Self-Assessment": "bg-slate-600",
};

const MatrixQuestion = ({ areas, value, onChange, questionId }) => {
  const matrixData = value || {};

  const handleToggleInterest = (area) => {
    const current = matrixData[area] || { interested: false, rating: 3 };
    const updated = { ...matrixData, [area]: { ...current, interested: !current.interested } };
    onChange(updated);
  };

  const handleRating = (area, rating) => {
    const current = matrixData[area] || { interested: false, rating: 3 };
    const updated = { ...matrixData, [area]: { ...current, rating } };
    onChange(updated);
  };

  return (
    <div className="space-y-1" data-testid={`matrix-${questionId}`}>
      {/* Header */}
      <div className="hidden sm:grid grid-cols-[1fr,100px,200px] gap-4 px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 border-b border-stone-200">
        <span>Leadership Area</span>
        <span className="text-center">Want to Improve?</span>
        <span className="text-center">Self-Rating</span>
      </div>

      {areas.map((area, i) => {
        const data = matrixData[area] || { interested: false, rating: 0 };
        return (
          <div
            key={area}
            className={`grid grid-cols-1 sm:grid-cols-[1fr,100px,200px] gap-3 sm:gap-4 items-center px-4 py-3 rounded-lg transition-colors ${
              data.interested ? "bg-orange-50 border border-orange-200" : i % 2 === 0 ? "bg-stone-50" : "bg-white"
            }`}
            data-testid={`matrix-row-${i}`}
          >
            <span className="font-medium text-sm text-slate-800">{area}</span>
            
            {/* Interest toggle */}
            <div className="flex items-center justify-center gap-2 sm:gap-0">
              <span className="text-xs text-slate-500 sm:hidden">Improve?</span>
              <button
                onClick={() => handleToggleInterest(area)}
                className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                  data.interested
                    ? "bg-orange-500 text-white"
                    : "bg-stone-200 text-stone-400 hover:bg-stone-300"
                }`}
                data-testid={`matrix-interest-${i}`}
              >
                <CheckCircle2 className="h-5 w-5" />
              </button>
            </div>

            {/* Rating */}
            <div className="flex items-center justify-center gap-1">
              <span className="text-xs text-slate-500 sm:hidden mr-2">Rating:</span>
              {[1, 2, 3, 4, 5].map((r) => (
                <button
                  key={r}
                  onClick={() => handleRating(area, r)}
                  className={`w-9 h-9 rounded-lg text-sm font-bold transition-all ${
                    data.rating === r
                      ? "bg-slate-800 text-white scale-110"
                      : data.rating > 0 && r <= data.rating
                        ? "bg-slate-300 text-white"
                        : "bg-stone-100 text-slate-400 hover:bg-stone-200"
                  }`}
                  data-testid={`matrix-rating-${i}-${r}`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
        );
      })}

      <div className="flex items-center justify-between px-4 pt-3 text-xs text-slate-400">
        <span>1 = Needs Work</span>
        <span>5 = Excellent</span>
      </div>
    </div>
  );
};

const LikertScale = ({ value, onChange, questionId }) => {
  const labels = ["Rarely", "Sometimes", "Often", "Usually", "Almost Always"];
  return (
    <div className="flex flex-col gap-3">
      {labels.map((label, i) => {
        const val = i + 1;
        const isSelected = value === val;
        return (
          <button
            key={val}
            onClick={() => onChange(val)}
            className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
              isSelected
                ? "border-orange-500 bg-orange-50"
                : "border-stone-200 hover:border-stone-300 bg-white"
            }`}
            data-testid={`likert-${questionId}-${val}`}
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              isSelected ? "bg-orange-500 text-white" : "bg-stone-100 text-slate-500"
            }`}>
              {val}
            </div>
            <span className={`font-medium ${isSelected ? "text-orange-700" : "text-slate-600"}`}>
              {label}
            </span>
          </button>
        );
      })}
    </div>
  );
};

const MultipleChoice = ({ options, value, onChange, questionId }) => {
  return (
    <div className="flex flex-col gap-3">
      {options.map((option, i) => {
        const isSelected = value === option.value;
        return (
          <button
            key={i}
            onClick={() => onChange(option.value)}
            className={`flex items-start gap-4 p-4 rounded-xl border-2 transition-all text-left ${
              isSelected
                ? "border-orange-500 bg-orange-50"
                : "border-stone-200 hover:border-stone-300 bg-white"
            }`}
            data-testid={`mc-${questionId}-${i}`}
          >
            <div className={`w-6 h-6 rounded-full flex-shrink-0 mt-0.5 flex items-center justify-center ${
              isSelected ? "bg-orange-500" : "border-2 border-stone-300"
            }`}>
              {isSelected && <CheckCircle2 className="h-4 w-4 text-white" />}
            </div>
            <span className={`${isSelected ? "text-orange-700 font-medium" : "text-slate-600"}`}>
              {option.text}
            </span>
          </button>
        );
      })}
    </div>
  );
};

const ResultsView = ({ results }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      // Submit with email to capture lead
      await axios.post(`${API}/quiz/submit`, {
        answers: [],
        name,
        email
      });
      setEmailSent(true);
      toast.success("We'll send your detailed results shortly!");
    } catch {
      setEmailSent(true);
      toast.success("Thanks! We'll be in touch.");
    }
    setSubmitting(false);
  };

  const packageMap = {
    "Base Camp": { color: "bg-slate-800", link: "/#services" },
    "Summit": { color: "bg-orange-600", link: "/#services" },
    "Peak Performance": { color: "bg-emerald-700", link: "/#services" },
  };

  const pkg = packageMap[results.recommended_package] || packageMap["Summit"];

  return (
    <div className="max-w-3xl mx-auto animate-fade-in" data-testid="quiz-results">
      {/* Score Header */}
      <div className="text-center mb-10">
        <div className="inline-flex items-center justify-center w-28 h-28 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 text-white mb-6">
          <span className="text-4xl font-bold">{results.overall_score}</span>
        </div>
        <h2 className="text-3xl md:text-4xl font-serif font-medium text-slate-900 mb-2">
          {results.level}
        </h2>
        <p className="text-lg text-slate-600 max-w-xl mx-auto">
          {results.summary}
        </p>
      </div>

      {/* Category Breakdown */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="text-lg">Your Leadership Profile</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {Object.entries(results.category_scores).map(([category, score]) => {
            const Icon = categoryIcons[category] || Target;
            const color = categoryColors[category] || "bg-slate-500";
            return (
              <div key={category} data-testid={`category-score-${category}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 ${color} rounded-lg flex items-center justify-center`}>
                      <Icon className="h-4 w-4 text-white" />
                    </div>
                    <span className="font-medium text-slate-800 text-sm">{category}</span>
                  </div>
                  <span className="font-bold text-slate-900">{score}%</span>
                </div>
                <div className="h-3 bg-stone-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${color} rounded-full transition-all duration-1000`}
                    style={{ width: `${score}%` }}
                  />
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Recommended Package */}
      <Card className="mb-8 border-2 border-orange-200 bg-orange-50">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <Mountain className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-orange-600 uppercase tracking-wide mb-1">Recommended For You</p>
              <h3 className="text-xl font-serif font-medium text-slate-900 mb-2">
                {results.recommended_package} Package
              </h3>
              <p className="text-slate-600 text-sm mb-4">
                Based on your assessment, this coaching engagement is the best fit for where you are in your leadership journey.
              </p>
              <Link to="/#contact">
                <Button className="bg-orange-600 hover:bg-orange-500" data-testid="quiz-cta-package">
                  Reach Out for More Info
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Capture */}
      {!emailSent ? (
        <Card className="bg-slate-800 text-white">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="h-6 w-6 text-orange-400" />
              <h3 className="text-lg font-semibold">Get Your Detailed Report</h3>
            </div>
            <p className="text-white/80 mb-6">
              Enter your email to receive a personalized leadership report with specific 
              recommendations and resources tailored to your results.
            </p>
            <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-3">
              <Input
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50 h-12"
                data-testid="quiz-email-name"
              />
              <Input
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50 h-12"
                data-testid="quiz-email-input"
              />
              <Button type="submit" disabled={submitting} className="bg-orange-600 hover:bg-orange-500 h-12 px-6 whitespace-nowrap" data-testid="quiz-email-submit">
                {submitting ? "Sending..." : "Send Report"}
              </Button>
            </form>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Report on its way!</h3>
            <p className="text-slate-600">Check your inbox for your personalized leadership report.</p>
          </CardContent>
        </Card>
      )}

      {/* Take Again / Home */}
      <div className="flex justify-center gap-4 mt-8">
        <Link to="/quiz">
          <Button variant="outline" onClick={() => window.location.reload()} data-testid="quiz-retake">
            Retake Quiz
          </Button>
        </Link>
        <Link to="/">
          <Button variant="ghost">Back to Home</Button>
        </Link>
      </div>
    </div>
  );
};

const QuizPage = () => {
  const [questions, setQuestions] = useState([]);
  const [currentStep, setCurrentStep] = useState(0); // 0 = intro
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/quiz/questions`).then(res => {
      setQuestions(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const totalSteps = questions.length + 1; // +1 for intro
  const progress = currentStep === 0 ? 0 : (currentStep / questions.length) * 100;
  const currentQuestion = currentStep > 0 ? questions[currentStep - 1] : null;

  const handleAnswer = (value) => {
    if (!currentQuestion) return;
    setAnswers(prev => ({ ...prev, [currentQuestion.id]: value }));
  };

  const handleNext = () => {
    if (currentStep < questions.length) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const answerList = Object.entries(answers).map(([question_id, answer]) => {
        // Check if this is a matrix answer (object with area keys)
        if (typeof answer === 'object' && !Array.isArray(answer)) {
          return { question_id, answer: null, matrix_data: answer };
        }
        return { question_id, answer };
      });
      const res = await axios.post(`${API}/quiz/submit`, { answers: answerList });
      setResults(res.data);
    } catch (err) {
      toast.error("Failed to submit quiz. Please try again.");
    }
    setSubmitting(false);
  };

  const isLastQuestion = currentStep === questions.length;
  const hasCurrentAnswer = currentQuestion && (() => {
    const ans = answers[currentQuestion.id];
    if (currentQuestion.type === 'matrix') {
      // Matrix is optional — always allow proceeding
      return true;
    }
    return ans !== undefined;
  })();

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 pt-20 flex items-center justify-center">
        <p className="text-slate-500">Loading quiz...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pt-20" data-testid="quiz-page">
      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Results */}
        {results ? (
          <ResultsView results={results} />
        ) : currentStep === 0 ? (
          /* Intro */
          <div className="text-center animate-fade-in" data-testid="quiz-intro">
            <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-8">
              <Target className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-serif font-medium text-slate-900 mb-4">
              Free Leadership Assessment
            </h1>
            <p className="text-lg text-slate-600 max-w-xl mx-auto mb-4">
              Discover your leadership strengths and growth areas in under 3 minutes. 
              Get personalized insights across 5 key dimensions of effective leadership.
            </p>
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {Object.entries(categoryIcons).map(([cat, Icon]) => (
                <div key={cat} className="flex items-center gap-2 bg-white px-3 py-2 rounded-full border border-stone-200 text-sm text-slate-600">
                  <Icon className="h-4 w-4 text-orange-500" />
                  {cat}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-center gap-6 text-sm text-slate-500 mb-10">
              <span>10 questions</span>
              <span className="w-1 h-1 bg-slate-300 rounded-full" />
              <span>~3 minutes</span>
              <span className="w-1 h-1 bg-slate-300 rounded-full" />
              <span>Instant results</span>
            </div>
            <Button
              size="lg"
              onClick={() => setCurrentStep(1)}
              className="bg-orange-600 hover:bg-orange-500 text-base font-bold px-10"
              data-testid="quiz-start-btn"
            >
              Start Assessment
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </div>
        ) : (
          /* Questions */
          <div className="animate-fade-in" data-testid={`quiz-step-${currentStep}`}>
            {/* Progress */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-500">
                  Question {currentStep} of {questions.length}
                </span>
                <span className="text-sm font-bold text-orange-600">
                  {Math.round(progress)}%
                </span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            {/* Category Badge */}
            {currentQuestion && (
              <div className="mb-6">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold text-white ${categoryColors[currentQuestion.category] || "bg-slate-500"}`}>
                  {(() => { const Icon = categoryIcons[currentQuestion.category] || Target; return <Icon className="h-3 w-3" />; })()}
                  {currentQuestion.category}
                </span>
              </div>
            )}

            {/* Question */}
            <Card className="mb-8">
              <CardContent className={currentQuestion?.type === "matrix" ? "p-4 sm:p-6" : "p-8"}>
                <h2 className="text-xl md:text-2xl font-serif font-medium text-slate-900 mb-2">
                  {currentQuestion?.question}
                </h2>
                {currentQuestion?.description && (
                  <p className="text-sm text-slate-500 mb-6">{currentQuestion.description}</p>
                )}

                <div className="mt-6">
                  {currentQuestion?.type === "matrix" ? (
                    <MatrixQuestion
                      areas={currentQuestion?.areas || []}
                      value={answers[currentQuestion.id]}
                      onChange={handleAnswer}
                      questionId={currentQuestion.id}
                    />
                  ) : currentQuestion?.type === "likert" ? (
                    <LikertScale
                      value={answers[currentQuestion.id]}
                      onChange={handleAnswer}
                      questionId={currentQuestion.id}
                    />
                  ) : (
                    <MultipleChoice
                      options={currentQuestion?.options || []}
                      value={answers[currentQuestion?.id]}
                      onChange={handleAnswer}
                      questionId={currentQuestion?.id}
                    />
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={handlePrev}
                disabled={currentStep <= 1}
                data-testid="quiz-prev"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Previous
              </Button>

              {isLastQuestion ? (
                <Button
                  onClick={handleSubmit}
                  disabled={!hasCurrentAnswer || submitting}
                  className="bg-orange-600 hover:bg-orange-500 px-8"
                  data-testid="quiz-submit"
                >
                  {submitting ? "Calculating..." : "See My Results"}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  disabled={!hasCurrentAnswer}
                  className="bg-slate-800 hover:bg-slate-700"
                  data-testid="quiz-next"
                >
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizPage;
